import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1小时（毫秒）

interface User {
  id: number;
  username: string;
  name: string;
  role: string;
  group_name: string | null;
  subjects: string | null;
  status: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 记录最后活跃时间
  const updateLastActivity = () => {
    localStorage.setItem('last_activity', Date.now().toString());
  };

  // 检查是否超时
  const checkInactivity = () => {
    const lastActivity = localStorage.getItem('last_activity');
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity);
      if (elapsed > INACTIVITY_TIMEOUT) {
        return true;
      }
    }
    return false;
  };

  // 监听用户活动事件
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handler = () => updateLastActivity();
    events.forEach(event => window.addEventListener(event, handler));
    updateLastActivity();
    return () => {
      events.forEach(event => window.removeEventListener(event, handler));
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // 先检查不活跃时间是否超过1小时
      if (checkInactivity()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('last_activity');
        setLoading(false);
        return;
      }
      api.get('/auth/me')
        .then(res => {
          setUser(res.data.user);
          updateLastActivity();
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('last_activity');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const res = await api.post('/auth/login', { username, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('role', res.data.user.role);
      localStorage.setItem('group_name', res.data.user.group_name || '');
      updateLastActivity();
      setUser(res.data.user);
    } catch (error: any) {
      const msg = error.response?.data?.error || '登录失败';
      throw new Error(msg);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    localStorage.removeItem('group_name');
    localStorage.removeItem('last_activity');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
